import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './shared/context/AuthContext';
import { CompanyProvider } from './shared/context/CompanyContext';

// Layout (kept synchronous — always visible)
import TopNav from './shell/TopNav';
import ProductSidebar from './shell/ProductSidebar';
import AIPanel from './shell/AIPanel';

// Auth pages (kept synchronous — initial entry points)
import TLLogin from './core/pages/auth/TLLogin';

// F1: Lazy-load all route-level pages to reduce initial bundle
const Register = React.lazy(() => import('./core/pages/auth/Register'));
const VerifyEmail = React.lazy(() => import('./core/pages/auth/VerifyEmail'));
const Onboarding = React.lazy(() => import('./core/pages/platform/Onboarding'));
const PackageSelection = React.lazy(() => import('./core/pages/platform/PackageSelection'));
const Provisioning = React.lazy(() => import('./core/pages/platform/Provisioning'));
const PrivacyPolicy = React.lazy(() => import('./core/pages/platform/PrivacyPolicy'));
const ContactPage = React.lazy(() => import('./core/pages/platform/ContactPage'));
const AuthCallback = React.lazy(() => import('./core/pages/auth/AuthCallback'));
const LoginPage = React.lazy(() => import('./core/pages/auth/LoginPage'));

// App pages — original pages
const TeamManagement = React.lazy(() => import('./core/pages/settings/TeamManagement'));
const TenantSettings = React.lazy(() => import('./core/pages/settings/TenantSettings'));
const DomainManagement = React.lazy(() => import('./core/pages/settings/DomainManagement'));
const HelpSupport = React.lazy(() => import('./core/pages/settings/HelpSupport'));
const ProfilePage = React.lazy(() => import('./core/pages/settings/ProfilePage'));
const AccountSettings = React.lazy(() => import('./core/pages/settings/AccountSettings'));
const PeerGroup = React.lazy(() => import('./core/pages/settings/PeerGroup'));
const AttackSurface = React.lazy(() => import('./products/surface/pages/_legacy_AttackSurface'));
const Vulnerabilities = React.lazy(() => import('./products/surface/pages/_legacy_Vulnerabilities'));
const DataExposure = React.lazy(() => import('./products/surface/pages/_legacy_DataExposure'));
const ComingSoon = React.lazy(() => import('./core/pages/platform/ComingSoon'));

// NEW v2 redesigned pages (PentestAI-inspired design)
const NewExecutiveSummary = React.lazy(() => import('./products/surface/pages/ExecutiveSummary'));
const NewAttackSurface = React.lazy(() => import('./products/surface/pages/AttackSurface'));
const DigitalFootprintPage = React.lazy(() => import('./products/surface/pages/DigitalFootprintPage'));
const NewVulnerabilities = React.lazy(() => import('./products/surface/pages/Vulnerabilities'));
const NewDataExposure = React.lazy(() => import('./products/surface/pages/DataExposure'));
const LensGuardAIPage = React.lazy(() => import('./products/surface/pages/LensGuardAIPage'));
const NewAttackPaths = React.lazy(() => import('./products/surface/pages/AttackPaths'));
const TLDScopePage = React.lazy(() => import('./products/surface/pages/TLDScopePage'));
const AssetProfilePage = React.lazy(() => import('./products/surface/pages/AssetProfilePage'));
const UnifiedFindingsPage = React.lazy(() => import('./products/workflow/pages/UnifiedFindingsPage'));
const EvidenceArtifactsPage = React.lazy(() => import('./products/workflow/pages/EvidenceArtifactsPage'));
const RemediationWorkspacePage = React.lazy(() => import('./products/workflow/pages/RemediationWorkspacePage'));
const ValidationJobsPage = React.lazy(() => import('./products/validate/pages/ValidationJobsPage'));
const RiskCompliancePage = React.lazy(() => import('./products/risk/pages/RiskCompliancePage'));
const IntegrationsPage = React.lazy(() => import('./core/pages/platform/IntegrationsPage'));
const CredentialIntelPage = React.lazy(() => import('./products/vigilance/pages/CredentialIntel'));
const BrandProtectionPage = React.lazy(() => import('./products/vigilance/pages/BrandProtection'));
const ThreatLandscapePage = React.lazy(() => import('./products/vigilance/pages/ThreatLandscape'));
const IntelFeedPage = React.lazy(() => import('./products/vigilance/pages/IntelFeed'));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const UpgradeGate = React.lazy(() => import('./shared/components/UpgradeGate'));

function TierGate({ requiredTier, children }) {
  const { hasVigilance } = useAuth();
  if (requiredTier === 'vigilance' && !hasVigilance) {
    return <React.Suspense fallback={null}><UpgradeGate requiredTier="vigilance" featureName="ThreatLens Vigilance" /></React.Suspense>;
  }
  if (requiredTier === 'strike') {
    return <React.Suspense fallback={null}><UpgradeGate requiredTier="strike" featureName="ThreatLens Strike" /></React.Suspense>;
  }
  return children;
}

function AppLayout({ children }) {
  const isStaging = process.env.REACT_APP_ENV === 'staging';
  // Staging banner adds 24px (top-6) above the topnav (48px) = 72px total
  const mainTop = isStaging ? 'mt-[72px]' : 'mt-topnav';
  return (
    <CompanyProvider>
      <div className="flex flex-col h-screen bg-white dark:bg-navy-900 overflow-hidden" data-testid="app-layout">
        <TopNav />
        <div className="flex flex-1 overflow-hidden">
          <ProductSidebar />
          <main className={`flex-1 overflow-y-auto p-4 lg:p-6 ${mainTop} ml-[260px] bg-white dark:bg-navy-900`}>
            {children}
          </main>
        </div>
        <AIPanel />
      </div>
    </CompanyProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full" /></div>}>
        <Routes>
          {/* Public routes */}
          <Route path="/register" element={<Register />} />
          <Route path="/login" element={<TLLogin />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth" element={<LoginPage />} />

          {/* Onboarding */}
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/packages" element={<ProtectedRoute><PackageSelection /></ProtectedRoute>} />
          <Route path="/provisioning" element={<ProtectedRoute><AppLayout><Provisioning /></AppLayout></ProtectedRoute>} />

          {/* Main app — Surface product */}
          <Route path="/dashboard" element={<ProtectedRoute><AppLayout><NewExecutiveSummary /></AppLayout></ProtectedRoute>} />
          <Route path="/attack-paths" element={<Navigate to="/new/surface/attack-paths" replace />} />

          {/* Settings */}
          <Route path="/settings/team" element={<ProtectedRoute><AppLayout><TeamManagement /></AppLayout></ProtectedRoute>} />
          <Route path="/settings/domains" element={<ProtectedRoute><AppLayout><DomainManagement /></AppLayout></ProtectedRoute>} />
          <Route path="/settings/profile" element={<ProtectedRoute><AppLayout><ProfilePage /></AppLayout></ProtectedRoute>} />
          <Route path="/settings/account" element={<ProtectedRoute><AppLayout><AccountSettings /></AppLayout></ProtectedRoute>} />
          <Route path="/settings/billing" element={<ProtectedRoute><AppLayout><TenantSettings /></AppLayout></ProtectedRoute>} />
          <Route path="/settings/peer-group" element={<ProtectedRoute><AppLayout><PeerGroup /></AppLayout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><AppLayout><TenantSettings /></AppLayout></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><AppLayout><HelpSupport /></AppLayout></ProtectedRoute>} />


          {/* Surface */}
          <Route path="/surface/attack-surface" element={<ProtectedRoute><AppLayout><AttackSurface /></AppLayout></ProtectedRoute>} />
          <Route path="/surface/vulnerabilities" element={<ProtectedRoute><AppLayout><Vulnerabilities /></AppLayout></ProtectedRoute>} />
          <Route path="/surface/data-exposure" element={<ProtectedRoute><AppLayout><DataExposure /></AppLayout></ProtectedRoute>} />

          {/* Vigilance — coming soon (same UX as Simulate / Sentinel) */}
          <Route path="/new/vigilance/*" element={<ProtectedRoute><AppLayout><ComingSoon /></AppLayout></ProtectedRoute>} />
          <Route path="/vigilance/*" element={<ProtectedRoute><AppLayout><ComingSoon /></AppLayout></ProtectedRoute>} />
          <Route path="/vigilance" element={<ProtectedRoute><AppLayout><ComingSoon /></AppLayout></ProtectedRoute>} />

          {/* LensGuard AI page */}
          <Route path="/lensguard-ai" element={<ProtectedRoute><AppLayout><LensGuardAIPage /></AppLayout></ProtectedRoute>} />

          {/* NEW v2 Surface */}
          <Route path="/new/dashboard" element={<ProtectedRoute><AppLayout><NewExecutiveSummary /></AppLayout></ProtectedRoute>} />
          <Route path="/new/surface/tld-scope" element={<ProtectedRoute><AppLayout><TLDScopePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/surface/digital-footprint" element={<ProtectedRoute><AppLayout><DigitalFootprintPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/surface/assets/profile" element={<ProtectedRoute><AppLayout><AssetProfilePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/surface/attack-paths" element={<ProtectedRoute><AppLayout><NewAttackPaths /></AppLayout></ProtectedRoute>} />
          <Route path="/new/surface/attack-surface" element={<ProtectedRoute><AppLayout><NewAttackSurface /></AppLayout></ProtectedRoute>} />
          <Route path="/new/surface/vulnerabilities" element={<ProtectedRoute><AppLayout><NewVulnerabilities /></AppLayout></ProtectedRoute>} />
          <Route path="/new/surface/data-exposure" element={<ProtectedRoute><AppLayout><NewDataExposure /></AppLayout></ProtectedRoute>} />

          {/* Five-pillar IA routes */}
          <Route path="/new/exposure/data-exposure" element={<ProtectedRoute><AppLayout><NewDataExposure /></AppLayout></ProtectedRoute>} />
          <Route path="/new/exposure/credential-intel" element={<ProtectedRoute><AppLayout><CredentialIntelPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/exposure/code-exposure" element={<ProtectedRoute><AppLayout><NewDataExposure /></AppLayout></ProtectedRoute>} />
          <Route path="/new/exposure/brand-fraud" element={<ProtectedRoute><AppLayout><BrandProtectionPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/exposure/threat-actors" element={<ProtectedRoute><AppLayout><ThreatLandscapePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/exposure/intel-feed" element={<ProtectedRoute><AppLayout><IntelFeedPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/exposure/*" element={<ProtectedRoute><AppLayout><NewDataExposure /></AppLayout></ProtectedRoute>} />
          <Route path="/new/risk/cyber-rating" element={<ProtectedRoute><AppLayout><RiskCompliancePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/risk/compliance" element={<ProtectedRoute><AppLayout><RiskCompliancePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/risk/prioritization" element={<ProtectedRoute><AppLayout><UnifiedFindingsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/risk/financial-impact" element={<ProtectedRoute><AppLayout><RiskCompliancePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/risk/reports" element={<ProtectedRoute><AppLayout><RiskCompliancePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/risk/*" element={<ProtectedRoute><AppLayout><RiskCompliancePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/validate/jobs" element={<ProtectedRoute><AppLayout><ValidationJobsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/validate/proof-of-fix" element={<ProtectedRoute><AppLayout><ValidationJobsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/validate/policy" element={<ProtectedRoute><AppLayout><ValidationJobsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/validate/history" element={<ProtectedRoute><AppLayout><ValidationJobsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/validate/*" element={<ProtectedRoute><AppLayout><ValidationJobsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/workflow/findings" element={<ProtectedRoute><AppLayout><UnifiedFindingsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/workflow/evidence" element={<ProtectedRoute><AppLayout><EvidenceArtifactsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/workflow/remediation" element={<ProtectedRoute><AppLayout><RemediationWorkspacePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/workflow/tickets" element={<ProtectedRoute><AppLayout><RemediationWorkspacePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/workflow/policies" element={<ProtectedRoute><AppLayout><RemediationWorkspacePage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/workflow/trust" element={<ProtectedRoute><AppLayout><EvidenceArtifactsPage /></AppLayout></ProtectedRoute>} />
          <Route path="/new/workflow/*" element={<ProtectedRoute><AppLayout><RemediationWorkspacePage /></AppLayout></ProtectedRoute>} />

          {/* Integrations */}
          <Route path="/integrations" element={<ProtectedRoute><AppLayout><IntegrationsPage /></AppLayout></ProtectedRoute>} />

          {/* Strike (upgrade gate) */}
          <Route path="/strike/*" element={<ProtectedRoute><AppLayout><TierGate requiredTier="strike"><ComingSoon /></TierGate></AppLayout></ProtectedRoute>} />
          <Route path="/strike" element={<ProtectedRoute><AppLayout><TierGate requiredTier="strike"><ComingSoon /></TierGate></AppLayout></ProtectedRoute>} />

          {/* Simulate (coming soon) */}
          <Route path="/simulate/*" element={<ProtectedRoute><AppLayout><ComingSoon /></AppLayout></ProtectedRoute>} />
          <Route path="/simulate" element={<ProtectedRoute><AppLayout><ComingSoon /></AppLayout></ProtectedRoute>} />

          {/* Sentinel (coming soon) */}
          <Route path="/sentinel/*" element={<ProtectedRoute><AppLayout><ComingSoon /></AppLayout></ProtectedRoute>} />
          <Route path="/sentinel" element={<ProtectedRoute><AppLayout><ComingSoon /></AppLayout></ProtectedRoute>} />

          <Route path="/payment-success" element={<ProtectedRoute><Provisioning /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </React.Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
